// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import Truck from "@insite/mobius/Icons/Truck";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import AlertCircle from "@store/Library/Components/Toolkit/Icons/AlertCircle/AlertCircleLine";
import { loadingDotsStyles } from "@store/Themes/common/mixins/typography";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface FullTruckLoadModel {
    plant?: string;
    ftlQuantity?: number | string;
}

interface OwnProps extends ExpandStyles<ProductFullTruckLoadStyles> {
    displayIcons?: boolean;
    displayTexts?: boolean;
    displaySeparator?: boolean;
    overrideFtl?: FullTruckLoadModel;
    isProductContext?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    isProductPage: getCurrentPage(state).type === "ProductDetailsPage",
});

type Props = OwnProps & HasProduct & ReturnType<typeof mapStateToProps>;

interface FullTruckLoadMessage {
    text?: TypographyPresentationProps;
    tooltip?: TooltipPresentationProps;
}

export interface ProductFullTruckLoadStyles {
    loadingText?: TypographyPresentationProps;
    fullTruckLoadMessage?: FullTruckLoadMessage;
    additionalInfoLabel?: TypographyPresentationProps;
    additionalInfoText?: InjectableCss;
    ftlWrapper?: InjectableCss;
    ftlIcon?: TooltipPresentationProps;
    pipeStyles?: TypographyPresentationProps;
}

export const productFullTruckLoadStyles: ProductFullTruckLoadStyles = {
    loadingText: {
        css: loadingDotsStyles(),
    },
    fullTruckLoadMessage: {
        text: {
            css: css`
                font-size: ${x => x.theme.design.typography.size.smaller};
                text-wrap: auto;
            `,
        },
        tooltip: {
            iconProps: {
                src: AlertCircle,
            },
        },
    },
    ftlWrapper: {
        css: css`
            align-items: center;
            gap: ${pxToRem(4)};
            font-size: ${x => x.theme.design.typography.size.smaller};
            margin-bottom: ${pxToRem(8)};
            display: flex;
            flex-direction: row;
        `,
    },
    additionalInfoLabel: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.smaller};
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    ftlIcon: {
        cssOverrides: {
            tooltipWrapper: css`
                ${x => x.theme.tooltip.defaultProps?.cssOverrides?.tooltipWrapper}
                margin-left: 0;
                text-wrap: auto;
            `,
        },
        iconProps: {
            src: Truck,
            css: css`
                margin-left: 0;
            `,
        },
    },
    additionalInfoText: {
        css: css`
            display: inherit;
        `,
    },
    pipeStyles: {
        color: "text.disabled",
        css: css`
            margin: ${pxToRem(0, 6)};
        `,
    },
};

const ProductFullTruckLoad = ({
    product,
    productInfo,
    extendedStyles,
    overridedStyles,
    displayIcons = false,
    displayTexts = true,
    displaySeparator = false,
    isProductPage,
    overrideFtl,
    isProductContext = false,
}: Props) => {
    const styles = useExpandProps(productFullTruckLoadStyles, extendedStyles, overridedStyles);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [fullTruckLoad, setFullTruckLoad] = useState<FullTruckLoadModel>();
    const [fullTruckLoadData] = useState<FullTruckLoadModel[]>([]);
    const displayIcon = isProductPage ? true : displayIcons;

    let fullTruckLoads;
    let pricing: ProductPriceDto | undefined;
    let defaultPlantNumber: string | undefined;
    let fullTruckLoadObject: any;

    if (isProductContext) {
        fullTruckLoads = product.properties.fullTruckLoads;
        pricing = productInfo.pricing;
        defaultPlantNumber = pricing?.additionalResults?.warehouseName;

        // Receiving data as a JSON string, needs to convert and extract properties
        fullTruckLoadObject = JSON.parse(fullTruckLoads);
    }

    useEffect(() => {
        if (isProductContext) {
            setIsLoading(!pricing);
        }
    }, [pricing, defaultPlantNumber]);

    useEffect(() => {
        if (isProductContext) {
            if (fullTruckLoadObject) {
                fullTruckLoadObject.forEach((fullTruckLoad: any) =>
                    fullTruckLoadData.push({
                        plant: fullTruckLoad.Plant,
                        ftlQuantity: fullTruckLoad.FullTruckLoad,
                    }),
                );
            }
        }
    }, [fullTruckLoadObject]);

    useEffect(() => {
        if (isProductContext) {
            setFullTruckLoad(
                defaultPlantNumber ? fullTruckLoadData.find(x => x.plant === defaultPlantNumber) : fullTruckLoadData[0],
            );
        }
    }, [defaultPlantNumber]);

    useEffect(() => {
        if (overrideFtl) {
            setFullTruckLoad(overrideFtl);
        }
    }, [overrideFtl]);

    if (fullTruckLoadObject?.length === 0 && isProductContext) {
        return null;
    }

    return (
        <>
            {displaySeparator && <Typography {...styles.pipeStyles}>|</Typography>}
            <StyledWrapper {...styles.ftlWrapper}>
                {displayIcon && (
                    <Tooltip {...styles.ftlIcon} text={siteMessage("ProductInfo_FtlIconMessage") as string} />
                )}
                <Typography {...styles.additionalInfoLabel}>
                    {displayTexts && `${translate("Full Truck Load")}${translate(":")}`}
                </Typography>
                <StyledWrapper {...styles.additionalInfoText}>
                    {isLoading ? (
                        <Typography {...styles.loadingText}>&nbsp;</Typography>
                    ) : (
                        ` ${fullTruckLoad?.ftlQuantity || translate("N/A")}`
                    )}
                    {!isLoading && (
                        <Tooltip
                            {...styles.fullTruckLoadMessage?.tooltip}
                            text={siteMessage("ProductInfo_FtlMessage") as string}
                        />
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

export default connect(mapStateToProps)(withProduct(ProductFullTruckLoad));
